// when there is a change in the file like try to add a blank space,
// then javascript is going to render again but the image src didn't change. So, the onLoad from <img> is not going to run
// which means that the image loader is not going to be set as false. To solve it we are using @refresh reset from Next.js
// it instructs Fast Refresh to remount components on every edit

// @refresh reset
import { Grid } from '@mui/material';
import classNames from 'classnames';
import { LikeButton } from 'components/buttons/LikeButton';
import A from 'components/Link/Link';
import config from 'config';
import StarIcon from 'public/icons/icn-star.svg';
import React, { createRef, ReactElement, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LazyLoad from 'react-lazyload';
import { useSelector } from 'react-redux';
import { setGlobalLoader } from 'store/actions/global-loader.actions';
import { likeServiceActions } from 'store/actions/like-service.actions';
import { StoreData } from 'store/root-reducer';
import { ProductImageType, ProductPriceDropType, ProductStatusType } from 'types/product.types';
import { blurhashToImage, fallbackBlurImage } from 'utils/blurhash';
import i18n from 'utils/i18n';
import ImageUrlResizer from 'utils/image-url-resizer';
import captureException from 'utils/sentry';

import { usePriceDropBadge } from '../../../../hooks/usePriceDropBadge';
import { PriceDropBadgeListTracking, trackPriceDropBadgeClick, usePriceDropBadgeInView } from '../../../../hooks/usePriceDropBadgeTracking';
import { usePriceFormatter } from '../../../../hooks/usePriceFormatter';
import { isBumped } from '../../../../utils/bumpedItems';
import { isNil } from '../../../../utils/is-nil';
import { BoostedBadge } from '../../../BoostedBadge/BoostedBadge';
import { NewListerBadge } from '../../../NewListerBadge/NewListerBadge';
import { PriceDropBadge } from '../../../PriceDropBadge/PriceDropBadge';

type Props = {
  src?: string;
  productImage?: ProductImageType;
  blurhash?: string | false; // value "false" will disable blurhash
  alt: string;
  price?: string | number;
  oldPrice?: string | number;
  priceDrop?: ProductPriceDropType | null;
  bumped?: boolean;
  productSlug?: string;
  priceDropTracking?: PriceDropBadgeListTracking;
  status?: ProductStatusType;
  showStatus?: boolean;
  details?: string | number; // Like size for product
  dark?: boolean; // Darker image e.g. for categories
  textOver?: string;
  md?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  xs?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  href?: string;
  as?: string;
  pinned?: boolean;
  blurHashWidth?: number;
  blurHashHeight?: number;
  stretch?: boolean; // stretch to 100% width
  wrapperClassName?: string;
  onClick?: (props: Props) => void;
  children?: ReactElement | ReactElement[];
  globalLoaderEnabled?: boolean; // Show global loader on click
  isCharity?: boolean;
  isGridItem?: boolean;
  showImageLoader?: boolean;
  isMainBlockImage?: boolean;
  productId?: number;
  shopId?: number;
  disableLazy?: boolean;
  isNewLister?: boolean;
  newListerBadgeProps?: Record<string, any>;
  brand?: string;
  tagged?: boolean;
  likeCount?: number;
  isOwner?: boolean;
  searchGuid?: string;
  recommendationId?: string;
  recommendationEngine?: string;
};

const useWebp = config.get('web.useWebpImages');
const BlockImage = (props: Props) => {
  const formatPrice = usePriceFormatter();

  const {
    src,
    productImage,
    blurhash,
    alt,
    price,
    oldPrice,
    priceDrop: productPriceDrop,
    bumped,
    productSlug,
    priceDropTracking,
    status,
    showStatus = true,
    details,
    dark,
    textOver,
    pinned,
    md,
    xs,
    href,
    as,
    blurHashWidth,
    blurHashHeight,
    stretch,
    wrapperClassName,
    onClick,
    globalLoaderEnabled,
    children,
    isCharity,
    isGridItem = true,
    showImageLoader = true, // set it to true if you want to show the image loader on every place
    isMainBlockImage,
    productId,
    disableLazy,
    isNewLister,
    newListerBadgeProps,
    brand,
    tagged,
    likeCount,
    isOwner,
    shopId,
    searchGuid,
    recommendationId,
    recommendationEngine,
  } = props;

  const { productLiked, currentUserShopId } = useSelector(({ userData }: StoreData) => ({
    productLiked: !!(userData?.user?.likedProducts && userData?.user?.likedProducts[productId]),
    currentUserShopId: userData?.user?.shop?.id,
  }));

  const isProductOwner = isOwner || (!!shopId && !!currentUserShopId && shopId === currentUserShopId);
  const priceDrop = usePriceDropBadge(productPriceDrop, status === 'sold' || !isNil(oldPrice));
  const showBumped = isBumped({ bumped, isOwner: isProductOwner }) && status !== 'sold';
  const struckPrice = priceDrop ? priceDrop.referencePrice : oldPrice;
  const priceDropProduct = { id: productId, slug: productSlug, price };

  const priceDropTileRef = useRef<HTMLDivElement>(null);

  usePriceDropBadgeInView(priceDropTileRef, priceDropTracking, priceDropProduct, priceDrop);

  const blurImageRef = createRef<HTMLImageElement>();
  const width = blurHashWidth || 100;
  const height = blurHashHeight || 100;
  const s3Image = (src || productImage?.thumbnail)?.includes('images.');

  const { t } = useTranslation();
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const [isLazyDisabled] = useState(disableLazy);

  // If none of the following props exists
  if (!src && !productImage) {
    console.error('src or productImage must be passed to <BlockImage /> element', src, productImage);
    captureException(`src or productImage missing in <BlockImage /> element, src: ${src}, imgObj: ${JSON.stringify(productImage || {})}`);
  }

  // Apply src blur image
  useEffect(() => {
    if (blurhash === false) {
      return;
    }

    if ((blurhash || productImage?.blurHash) && blurImageRef?.current?.src) {
      blurImageRef.current.src = blurhashToImage(blurhash || productImage?.blurHash, width, height);
    }
  }, [productImage, blurhash]);

  // Add image loading screen if the prop changes
  useEffect(() => {
    // when the src will change we are showing the image loader
    if (showImageLoader) {
      setIsLoadingImage(true);
    }

    changeImageVisibility();
  }, [src]);

  // adding some scss to images in useEffect to handle some rendering
  const changeImageVisibility = () => {
    const imgEl = imgRef?.current;

    if (imgEl) {
      imgEl.classList.remove('fade-in-image');
      isMainBlockImage ? (imgEl.style.opacity = '0') : (imgEl.style.opacity = '1');
    }
  };

  const [localLikeCount, setLocalLikeCount] = useState(() => likeCount || 0);
  const prevProductLiked = useRef(productLiked);

  useEffect(() => {
    if (prevProductLiked.current !== productLiked) {
      setLocalLikeCount((prev) => (productLiked ? prev + 1 : prev > 0 ? prev - 1 : 0));
      prevProductLiked.current = productLiked;
    }
  }, [productLiked]);

  const toggleLike = () => {
    likeServiceActions.toggleLikeState({
      liked: !productLiked,
      id: productId,
      ...(searchGuid
        ? { searchGuid }
        : { ...(recommendationId && { recommendationId }), ...(recommendationEngine && { recommendationEngine }) }),
    });
  };

  const picture = (
    <picture>
      {useWebp && s3Image && <source type="image/webp" srcSet={ImageUrlResizer.toWebp(productImage?.thumbnail || src)} />}
      <img
        src={productImage?.thumbnail || src}
        alt={alt}
        ref={imgRef}
        className={classNames('src-image', { 'lazy-image': !isLazyDisabled })}
        draggable={false}
        onLoad={(event: SyntheticEvent) => {
          if (!isLazyDisabled) {
            const target = event.target as HTMLImageElement;

            target.classList.add('lazy-image-loaded');
            isLoadingImage ? target.classList.add('fade-in-image') : '';
            target.style.opacity = '1';
          }
          setIsLoadingImage(false);
        }}
        onError={() => setIsLoadingImage(false)}
      />
    </picture>
  );

  const getProductImageContent = () => (
    <A
      href={href}
      as={as}
      className={classNames('no-style', { 'block-image__link': !isGridItem })}
      // Show global loader on click, if enabled
      onClick={(e) => {
        globalLoaderEnabled && setGlobalLoader(true, e);

        if (priceDrop && priceDropTracking) {
          trackPriceDropBadgeClick(priceDropTracking.listId, priceDropProduct, priceDrop);
        }
      }}
    >
      {/* image container */}
      <div
        className={classNames(
          { 'image-container': isGridItem },
          { 'image-link-container': !isGridItem },
          { dark },
          { 'image-container--overlay': !!productId },
        )}
      >
        {/* Not visible */}
        {status === 'not-visible' && (
          <span className="visibility-block">
            <span className="visibility-block__icon" />
            <span className="visibility-block__text">{t('components.page-product.product-details.hidden')}</span>
          </span>
        )}

        {/* image */}
        {isLazyDisabled ? (
          <div className="lazyload-wrapper">{picture}</div>
        ) : (
          <LazyLoad once={true} offset={50} placeholder={<img ref={blurImageRef} src={fallbackBlurImage} alt="" />}>
            {picture}
          </LazyLoad>
        )}

        {isNewLister && <NewListerBadge {...newListerBadgeProps} />}

        {(priceDrop || showBumped) && (
          <div className="tile-badges">
            {priceDrop && <PriceDropBadge percentage={priceDrop.percentage} />}
            {showBumped && <BoostedBadge />}
          </div>
        )}

        {/* pinned */}
        {showStatus && pinned && <i className="icon yaga-star pinned" />}

        {/* charity */}
        {showStatus && !!isCharity && <div className="charity-icon" />}

        {/* AI tagged product */}
        {showStatus && tagged && <StarIcon className="product-tagged" key="tag" />}

        {/* text over */}
        {textOver && <div className="text-over">{textOver}</div>}

        {/* sold */}
        {showStatus && status === 'sold' && <div className="sold-ribbon">{i18n.t('common.product-status.sold')}</div>}
        {/*  Here we overlay the loader on the image,
        we are doing it, this way because we don't know the image height */}
        {isLoadingImage && (
          <div className="loadingImageContainer">
            <div className="loader" />
          </div>
        )}

        {/* like button */}
        {productId && !pinned && (
          <div
            className={classNames('like-button-wrapper', {
              'like-button-wrapper--has-count': localLikeCount > 0,
            })}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            {localLikeCount > 0 && <span className="like-count">{localLikeCount}</span>}
            <LikeButton liked={productLiked} onClick={toggleLike} disabled={isProductOwner} />
          </div>
        )}
      </div>

      {/* info container */}
      {(price !== undefined || details || brand) && (
        <div className="pricebrand-container">
          <div className="price-container">
            {/* price */}
            {price !== undefined && (
              <h5 className="price">
                {formatPrice(price)}

                {/* old price */}
                {!isNil(struckPrice) && (
                  <span className={classNames('old-price', { 'old-price--tight': !!priceDrop })}>{formatPrice(struckPrice)}</span>
                )}
              </h5>
            )}

            {/* size */}
            {details && <h5 className="details">{details}</h5>}
          </div>
          {/* brand info container */}
          {brand && (
            <div className="brand-container">
              <h5 className="details">{brand}</h5>
            </div>
          )}
        </div>
      )}

      {children}
    </A>
  );

  // Grid props if image is considered to be a grid item.
  const gridProps = isGridItem
    ? {
        item: true,
        md: md || 2,
        xs: xs || 6,
      }
    : {
        component: 'div',
      };

  return (
    <>
      <Grid
        {...gridProps}
        ref={priceDropTileRef}
        className={classNames(
          'block-image',
          {
            hoverable: !!as || !!href || onClick,
            stretch,
          },
          wrapperClassName,
        )}
        onClick={() => {
          if (typeof onClick === 'function') {
            onClick(props);
          }
        }}
      >
        {getProductImageContent()}
      </Grid>
    </>
  );
};

export default BlockImage;
